import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { DetailCard } from "@/components/detail-page";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Building2,
  Users,
  Package,
  CreditCard,
  ArrowRight,
  Search,
  ChevronLeft,
  ChevronRight,
  Plus,
} from "lucide-react";
import { cn } from "@/lib/utils";

// --- Types ---

interface BusinessUnit {
  id: string;
  name: string;
  status: string;
  cardProgramName: string | null;
  email: string | null;
}

interface Customer {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  status: string;
  kycStatus: string;
}

interface Product {
  id: string;
  name: string;
  cardType: string;
  status: string;
  requestStatus: string;
}

interface Card {
  id: string;
  lastFour: string | null;
  cardType: string;
  status: string;
  nickname: string | null;
  expMonth: number | null;
  expYear: number | null;
}

type EntityType = "business-units" | "customers" | "products" | "cards";

interface InlineEntityListProps {
  type: EntityType;
  queryKey: string[];
  endpoint: string;
  title: string;
  description?: string;
  emptyMessage: string;
  linkPrefix: string;
  pageSize?: number;
  onCreate?: () => void;
  createLabel?: string;
}

// --- Helpers ---

const ENTITY_ICONS = {
  "business-units": Building2,
  customers: Users,
  products: Package,
  cards: CreditCard,
};

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  APPROVED: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  VERIFIED: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  PENDING: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  PENDING_REVIEW: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  DRAFT: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  INACTIVE: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  NOT_STARTED: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  REJECTED: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  SUSPENDED: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  FROZEN: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  CANCELLED: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  EXPIRED: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
};

const getStatusBadge = (status: string) => (
  <Badge className={cn("text-xs", STATUS_COLORS[status] || "")}>
    {status.replace(/_/g, " ")}
  </Badge>
);

// --- Main Component ---

export function InlineEntityList({
  type,
  queryKey,
  endpoint,
  title,
  description,
  emptyMessage,
  linkPrefix,
  pageSize = 10,
  onCreate,
  createLabel,
}: InlineEntityListProps) {
  const [, setLocation] = useLocation();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const Icon = ENTITY_ICONS[type];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await apiRequest("GET", endpoint);
      if (!response.ok) return [];
      return response.json();
    },
  });

  const items = data || [];

  // Filter by search term
  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const searchLower = search.toLowerCase();

    return items.filter((item: any) => {
      switch (type) {
        case "business-units":
          const ca = item as BusinessUnit;
          return (
            ca.name?.toLowerCase().includes(searchLower) ||
            ca.email?.toLowerCase().includes(searchLower) ||
            ca.cardProgramName?.toLowerCase().includes(searchLower)
          );
        case "customers":
          const cust = item as Customer;
          return (
            cust.firstName?.toLowerCase().includes(searchLower) ||
            cust.lastName?.toLowerCase().includes(searchLower) ||
            cust.email?.toLowerCase().includes(searchLower)
          );
        case "products":
          const prod = item as Product;
          return (
            prod.name?.toLowerCase().includes(searchLower) ||
            prod.cardType?.toLowerCase().includes(searchLower)
          );
        case "cards":
          const card = item as Card;
          return (
            card.lastFour?.includes(searchLower) ||
            card.nickname?.toLowerCase().includes(searchLower)
          );
        default:
          return true;
      }
    });
  }, [items, search, type]);

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / pageSize);
  const startIndex = (page - 1) * pageSize;
  const paginatedItems = filteredItems.slice(startIndex, startIndex + pageSize);

  // Reset page when search changes
  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const renderTable = () => {
    switch (type) {
      case "business-units":
        return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Card Program</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(paginatedItems as BusinessUnit[]).map((item) => (
                <TableRow
                  key={item.id}
                  className="cursor-pointer"
                  onClick={() => setLocation(`${linkPrefix}?select=${item.id}`)}
                >
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell className="text-muted-foreground">{item.email || "-"}</TableCell>
                  <TableCell>{item.cardProgramName || "-"}</TableCell>
                  <TableCell>{getStatusBadge(item.status)}</TableCell>
                  <TableCell>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      case "customers":
        return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>KYC Status</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(paginatedItems as Customer[]).map((item) => (
                <TableRow
                  key={item.id}
                  className="cursor-pointer"
                  onClick={() => setLocation(`${linkPrefix}?select=${item.id}`)}
                >
                  <TableCell className="font-medium">
                    {item.firstName} {item.lastName}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{item.email}</TableCell>
                  <TableCell>{getStatusBadge(item.kycStatus)}</TableCell>
                  <TableCell>{getStatusBadge(item.status)}</TableCell>
                  <TableCell>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      case "products":
        return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Card Type</TableHead>
                <TableHead>Request Status</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(paginatedItems as Product[]).map((item) => (
                <TableRow
                  key={item.id}
                  className="cursor-pointer"
                  onClick={() => setLocation(`${linkPrefix}?select=${item.id}`)}
                >
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{item.cardType}</Badge>
                  </TableCell>
                  <TableCell>{getStatusBadge(item.requestStatus)}</TableCell>
                  <TableCell>{getStatusBadge(item.status)}</TableCell>
                  <TableCell>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      case "cards":
        return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Card</TableHead>
                <TableHead>Nickname</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(paginatedItems as Card[]).map((item) => (
                <TableRow
                  key={item.id}
                  className="cursor-pointer"
                  onClick={() => setLocation(`${linkPrefix}?select=${item.id}`)}
                >
                  <TableCell className="font-mono">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-muted-foreground" />
                      **** {item.lastFour || "****"}
                    </div>
                  </TableCell>
                  <TableCell>{item.nickname || "-"}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{item.cardType}</Badge>
                  </TableCell>
                  <TableCell>
                    {item.expMonth && item.expYear ? `${item.expMonth}/${item.expYear}` : "-"}
                  </TableCell>
                  <TableCell>{getStatusBadge(item.status)}</TableCell>
                  <TableCell>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      default:
        return null;
    }
  };

  return (
    <DetailCard
      title={
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-muted-foreground" />
          {title}
          {!isLoading && (
            <Badge variant="secondary" className="ml-1">
              {items.length}
            </Badge>
          )}
        </div>
      }
      description={description}
      headerActions={
        onCreate && (
          <Button size="sm" onClick={onCreate}>
            <Plus className="h-4 w-4 mr-1" />
            {createLabel || "Create"}
          </Button>
        )
      }
    >
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12">
          <Icon className="mx-auto h-12 w-12 text-muted-foreground/50" />
          <p className="mt-2 text-sm text-muted-foreground">{emptyMessage}</p>
          {onCreate && (
            <Button size="sm" className="mt-4" onClick={onCreate}>
              <Plus className="h-4 w-4 mr-1" />
              {createLabel || "Create First"}
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Search */}
          {items.length > 5 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={`Search ${title.toLowerCase()}...`}
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9 max-w-sm"
              />
            </div>
          )}

          {/* Table */}
          {filteredItems.length > 0 ? (
            renderTable()
          ) : (
            <div className="text-center py-8">
              <Search className="mx-auto h-8 w-8 text-muted-foreground/50" />
              <p className="mt-2 text-sm text-muted-foreground">
                No results for "{search}"
              </p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {startIndex + 1}-{Math.min(startIndex + pageSize, filteredItems.length)} of{" "}
                {filteredItems.length}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground px-2">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </DetailCard>
  );
}

// Convenience wrappers for common use cases
export function BusinessUnitsList({
  organizationId,
  onCreate,
}: {
  organizationId: string;
  onCreate?: () => void;
}) {
  return (
    <InlineEntityList
      type="business-units"
      queryKey={["pm-business-units", organizationId]}
      endpoint={`/api/business-units?organizationId=${organizationId}`}
      title="Business Units"
      description="Business units managed under this program manager"
      emptyMessage="No business units under this program manager"
      linkPrefix="/business-unit"
      onCreate={onCreate}
      createLabel="Add Business Unit"
    />
  );
}

export function CustomersList({
  businessUnitId,
  onCreate,
}: {
  businessUnitId: string;
  onCreate?: () => void;
}) {
  return (
    <InlineEntityList
      type="customers"
      queryKey={["bu-customers", businessUnitId]}
      endpoint={`/api/customers?businessUnitId=${businessUnitId}`}
      title="Customers"
      description="Card holders managed under this business unit"
      emptyMessage="No customers under this business unit"
      linkPrefix="/customers"
      onCreate={onCreate}
      createLabel="Add Customer"
    />
  );
}

export function ProductsList({
  organizationId,
  onCreate,
}: {
  organizationId: string;
  onCreate?: () => void;
}) {
  return (
    <InlineEntityList
      type="products"
      queryKey={["pm-products", organizationId]}
      endpoint={`/api/products?organizationId=${organizationId}`}
      title="Products"
      description="Card products created by this program manager"
      emptyMessage="No products created by this program manager"
      linkPrefix="/products"
      onCreate={onCreate}
      createLabel="Create Product"
    />
  );
}

export function CardsList({
  productId,
  onCreate,
}: {
  productId: string;
  onCreate?: () => void;
}) {
  return (
    <InlineEntityList
      type="cards"
      queryKey={["product-cards", productId]}
      endpoint={`/api/virtual-cards?productId=${productId}`}
      title="Cards"
      description="Virtual cards issued under this product"
      emptyMessage="No cards issued under this product"
      linkPrefix="/cards"
      onCreate={onCreate}
      createLabel="Issue Card"
    />
  );
}
